import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { useBulkArchiveScenariosMutation } from "generated/graphql";
import { segmentTrackBulkArchiveScenarios } from "in_queue/analytics";
import { ScenarioMetadata, ScenarioOrigin } from "in_queue/types/scenarioType";
import { useCallback } from "react";

export interface BulkArchiveResult {
    requestedCount: number;
    archivedCount: number;
}

export const useBulkArchiveScenarios = ({
    projectId
}: {
    projectId: string;
}): {
    archiveScenarios: (
        scenarios: ReadonlyArray<ScenarioMetadata>
    ) => Promise<BulkArchiveResult>;
    isArchiving: boolean;
} => {
    const { teamId } = useUserDataContext();
    const [bulkArchiveScenarios, { loading }] =
        useBulkArchiveScenariosMutation();

    const archiveScenarios = useCallback(
        async (
            scenarios: ReadonlyArray<ScenarioMetadata>
        ): Promise<BulkArchiveResult> => {
            const isPublished = (scenario: ScenarioMetadata) =>
                scenario.origin === ScenarioOrigin.NiraPublished;
            const customScenarioIds = scenarios
                .filter((scenario) => !isPublished(scenario))
                .map((scenario) => scenario.scenarioId);
            const teamScenarioConfigurations = scenarios
                .filter(isPublished)
                .map((scenario) => ({
                    scenario_id: scenario.scenarioId,
                    team_id: teamId,
                    archived: true
                }));

            const response = await bulkArchiveScenarios({
                variables: {
                    customScenarioIds,
                    lastArchivedAt: new Date().toISOString(),
                    teamScenarioConfigurations
                }
            });

            const numPublishedScenarios =
                response.data?.insert_scenario_configurations?.affected_rows ??
                0;
            const archivedCount =
                (response.data?.update_scenarios?.affected_rows ?? 0) +
                numPublishedScenarios;

            if (archivedCount > 0) {
                segmentTrackBulkArchiveScenarios({
                    projectId,
                    numScenarios: archivedCount,
                    numPublishedScenarios
                });
            }

            return { requestedCount: scenarios.length, archivedCount };
        },
        [bulkArchiveScenarios, projectId, teamId]
    );

    return { archiveScenarios, isArchiving: loading };
};
